import React from 'react';
import { Alert, Button, Icon, Table } from '../common';
import ActionRow from '../common/tables/ActionRow';
import { TreeNavigation, TreeNavigationItem } from '@emorg-prd/standard-react';
import { UserRole } from '../../models/enums/UserRole';
import { authenticatedUser } from '../../authentication/authModule';
import WorkflowStateBadge from '../common/WorkflowStateBadge';
import moment from 'moment';
import CloudAccount from '../../models/CloudAccount';
import { WorkflowState } from '../../models/enums/WorkflowState';
import './CloudAccountsHomeTableRow.css';

interface CloudAccountsHomeTableRowProps {
	cloudAccount: CloudAccount;
	actionsEnabled: boolean;
	status: string;
	isMyAccount: boolean;
	setResetPasswordModalVisible: (visible: boolean) => void;
	setSelectedCloudAccount: (cloudAccount: CloudAccount) => void;
	setResetMfaModalVisible: (visible: boolean) => void;
	setDeleteAccountModalVisible: (visible: boolean) => void;
	setApproveModalVisible: (visible: boolean) => void;
	setRejectModalVisible: (visible: boolean) => void;
	setReasonModalVisible: (visible: boolean) => void;
	setRevalidateAccountModalVisible: (visible: boolean) => void;
	setCancelAccountModalVisible: (visible: boolean) => void;
}

const CloudAccountsHomeTableRow: React.FC<CloudAccountsHomeTableRowProps> = ({
	cloudAccount,
	actionsEnabled,
	status,
	isMyAccount,
	setResetPasswordModalVisible,
	setSelectedCloudAccount,
	setResetMfaModalVisible,
	setDeleteAccountModalVisible,
	setApproveModalVisible,
	setRejectModalVisible,
	setReasonModalVisible,
	setRevalidateAccountModalVisible,
	setCancelAccountModalVisible,
}) => {
	const actionRef: React.RefObject<HTMLBaseElement> = React.createRef();

	const canRevalidateAccount = (status: string, CloudAccount: CloudAccount): boolean => {
		const owner = CloudAccount.workflowInstance.owner;

		return (
			status === 'Error' &&
			(authenticatedUser.isInRole(UserRole.CloudAccountAdmin) || authenticatedUser.userId == owner.id)
		);
	};

	return (
		<Table.Row>
			<ActionRow
				key={cloudAccount.id}
				actionRef={actionRef}
				actionCell={cloudAccount.createdUserPrincipalName}
				actions={
					actionsEnabled && (
						<TreeNavigation>
							{status === WorkflowState.Approved.name && (
								<>
									<TreeNavigationItem
										label="Open in Azure"
										onClick={() => {
											window.open(
												`https://portal.azure.com/#blade/Microsoft_AAD_IAM/UserDetailsMenuBlade/Profile/userId/${cloudAccount.createdUserPrincipalName}`,
												'_blank'
											);
											actionRef.current?.click();
										}}
									/>
									<TreeNavigationItem
										label="Reset Password"
										onClick={() => {
											setResetPasswordModalVisible(true);
											setSelectedCloudAccount(cloudAccount);
										}}
									/>

									{!cloudAccount.createdUserPrincipalName.endsWith('@xomservices.com') &&
										cloudAccount.canResetMFA && (
											<TreeNavigationItem
												label="Reset MFA Configuration"
												onClick={() => {
													setResetMfaModalVisible(true);
													setSelectedCloudAccount(cloudAccount);
												}}
											/>
										)}

									<TreeNavigationItem
										label="Delete Account"
										onClick={() => {
											setDeleteAccountModalVisible(true);
											setSelectedCloudAccount(cloudAccount);
										}}
									/>
								</>
							)}
							{status === WorkflowState.Submitted.name &&
								(cloudAccount.approver == authenticatedUser.userEmail ||
									(authenticatedUser.isInRole(UserRole.CloudAccountAdmin) &&
										!cloudAccount.approver)) && (
									<>
										<TreeNavigationItem
											label="Approve"
											onClick={() => {
												setApproveModalVisible(true);
												setSelectedCloudAccount(cloudAccount);
											}}
										/>
										<TreeNavigationItem
											label="Reject"
											onClick={() => {
												setRejectModalVisible(true);
												setSelectedCloudAccount(cloudAccount);
											}}
										/>
									</>
								)}
							<TreeNavigationItem
								label="View Request Information"
								onClick={() => {
									setReasonModalVisible(true);
									setSelectedCloudAccount(cloudAccount);
								}}
							/>
							{canRevalidateAccount(status, cloudAccount) ? (
								<>
									<TreeNavigationItem
										label="Revalidate Account"
										onClick={() => {
											setRevalidateAccountModalVisible(true);
											setSelectedCloudAccount(cloudAccount);
										}}
									/>
									<Alert
										variant={'warning'}
										body="Your account is currently in an error state. If it was previously approved and you wish to restore its status, please use the 'Revalidate Account' option."
									/>
								</>
							) : (
								<></>
							)}
						</TreeNavigation>
					)
				}
			/>
			<Table.Cell>
				<WorkflowStateBadge
					workflowState={cloudAccount.workflowInstance.currentWorkflowInstanceState.workflowState}
					fullWidth
				/>
			</Table.Cell>
			<Table.Cell>{cloudAccount.approver ?? 'Entra ID Team'}</Table.Cell>
			<Table.Cell>
				{authenticatedUser.isInRole(UserRole.CloudAccountAdmin)
					? cloudAccount.workflowInstance.createdBy.displayName
					: cloudAccount.workflowInstance.currentWorkflowInstanceState.performedByUser.displayName}
			</Table.Cell>
			<Table.Cell>{moment(new Date(cloudAccount.workflowInstance.createdDate)).format('MM/DD/YYYY')}</Table.Cell>
			<Table.Cell>
				<Button
					style={{
						zIndex: 0,
						cursor: status === WorkflowState.Submitted.name ? 'pointer' : 'not-allowed',
					}}
					variant="primary"
					size="small"
					onClick={() => {
						setCancelAccountModalVisible(true);
						setSelectedCloudAccount(cloudAccount);
					}}
					disabled={status !== WorkflowState.Submitted.name || !isMyAccount}
				>
					<Icon size="small" icon="trashcan" />
				</Button>
			</Table.Cell>
		</Table.Row>
	);
};

export default CloudAccountsHomeTableRow;
